import React, {
  ButtonHTMLAttributes,
  ComponentType,
  forwardRef,
  ReactNode,
} from 'react';

import {
  ButtonContent,
  ButtonContentAlign,
  ButtonSize,
  ButtonVariant,
  getButtonClasses,
} from './utils';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variant: ButtonVariant;
  size?: ButtonSize;
  isFullWidth?: boolean;
  isLoading?: boolean;
  StartIcon?: ComponentType;
  EndIcon?: ComponentType;
  contentAlign?: ButtonContentAlign;
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      children,
      variant,
      size = ButtonSize.MEDIUM,
      isFullWidth,
      isLoading,
      StartIcon,
      EndIcon,
      contentAlign,
      className,
      disabled,
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ): JSX.Element => {
    const isDisabled = disabled || isLoading;
    const loadingAriaLabel =
      isLoading && ariaLabel === undefined && typeof children === 'string'
        ? children
        : ariaLabel;
    const classes = getButtonClasses({
      variant,
      size,
      isFullWidth,
      isLoading,
      hasStartIcon: Boolean(StartIcon),
      hasEndIcon: Boolean(EndIcon),
      disabled,
      contentAlign,
      className,
    });

    return (
      <button
        ref={ref}
        type="button"
        className={classes}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-label={loadingAriaLabel}
        {...props}
      >
        <ButtonContent
          size={size}
          isLoading={isLoading}
          StartIcon={StartIcon}
          EndIcon={EndIcon}
        >
          {children}
        </ButtonContent>
      </button>
    );
  },
);

Button.displayName = 'Button';

export type { Props as ButtonProps };
